import type { ReactNode } from "react";

import { Button } from "./Button";
import { ChevronLeft, ChevronRight } from "./icons";
import { cn } from "./cn";
import { useScrollFade } from "./useScrollFade";

export interface TableColumn<Row> {
  id: string;
  header: ReactNode;
  cell: (row: Row) => ReactNode;
}

export interface TablePagination {
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  previousLabel: string;
  nextLabel: string;
  pageLabel: string;
}

export interface TableProps<Row> {
  caption: string;
  columns: TableColumn<Row>[];
  rows: Row[];
  rowKey: (row: Row) => string;
  onRowClick?: (row: Row) => void;
  /** Rendered in place of the body when there is nothing to show. */
  empty?: ReactNode;
  /**
   * Keyset or page navigation. Large collections page rather than virtualise:
   * opening one record must not require loading the whole set.
   */
  pagination?: TablePagination;
}

/**
 * Narrow screens get a sticky first column plus horizontal scroll — the design
 * chose that over wrapping cells, so a row stays readable as one line.
 */
export function Table<Row>({
  caption,
  columns,
  rows,
  rowKey,
  onRowClick,
  empty,
  pagination,
}: TableProps<Row>) {
  const { ref: scrollRef, fadeVisible } = useScrollFade<HTMLDivElement>(
    rows.length === 0,
  );
  if (rows.length === 0 && empty !== undefined) return <>{empty}</>;
  return (
    <div className="flex flex-col gap-3">
      <div className="relative overflow-hidden rounded-xl bg-raised shadow-card">
        <div ref={scrollRef} className="overflow-x-auto">
          <table className="w-full border-collapse text-callout">
            <caption className="sr-only">{caption}</caption>
            <thead>
              <tr>
                {columns.map((column, index) => (
                  <th
                    key={column.id}
                    scope="col"
                    className={cn(
                      "bg-card-alt px-3.5 py-2.5 text-left text-caption font-semibold uppercase tracking-caps text-fg-muted",
                      index === 0 && "sticky left-0 z-10",
                    )}
                  >
                    {column.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr
                  key={rowKey(row)}
                  onClick={
                    onRowClick
                      ? () => {
                          onRowClick(row);
                        }
                      : undefined
                  }
                  className={cn(
                    // Even rows (1-based) carry the field fill; no vertical borders.
                    rowIndex % 2 === 1 && "bg-sunken/50",
                    onRowClick && "cursor-pointer hover:bg-hover-overlay",
                  )}
                >
                  {columns.map((column, index) => (
                    <td
                      key={column.id}
                      className={cn(
                        "px-3.5 py-3 text-fg",
                        index === 0 && "sticky left-0 z-10 font-semibold",
                        index === 0 &&
                          (rowIndex % 2 === 1
                            ? "bg-[color-mix(in_srgb,var(--surface-sunken)_50%,var(--surface-raised))]"
                            : "bg-raised"),
                      )}
                    >
                      {column.cell(row)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div
          aria-hidden="true"
          data-testid="table-scroll-fade"
          data-visible={fadeVisible}
          className={cn(
            "pointer-events-none absolute inset-y-0 right-0 z-20 w-6 bg-linear-to-r from-transparent to-raised",
            fadeVisible ? "opacity-100" : "opacity-0",
          )}
        />
      </div>
      {pagination && pagination.pageCount > 1 && (
        <nav
          aria-label={pagination.pageLabel}
          className="flex items-center justify-between gap-2"
        >
          <Button
            size="sm"
            variant="secondary"
            disabled={pagination.page <= 1}
            aria-label={pagination.previousLabel}
            onClick={() => {
              pagination.onPageChange(pagination.page - 1);
            }}
          >
            <ChevronLeft size={16} />
          </Button>
          <span className="text-footnote text-fg-muted">
            {pagination.pageLabel} {pagination.page} / {pagination.pageCount}
          </span>
          <Button
            size="sm"
            variant="secondary"
            disabled={pagination.page >= pagination.pageCount}
            aria-label={pagination.nextLabel}
            onClick={() => {
              pagination.onPageChange(pagination.page + 1);
            }}
          >
            <ChevronRight size={16} />
          </Button>
        </nav>
      )}
    </div>
  );
}
